import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { Affiliation } from '../_models/affiliation.model';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth';
import { environment } from 'src/environments/environment';
import { tagsViewContext } from './shared-http-request.service';
import { AffiliationType } from '../_models/affiliation-type.model';

@Injectable()
export class AffiliationsService {
  private authToken;
  private companyId: any;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  private static getAffiliationsPageQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = `page=${pageNumber}&size=${pageSize}&sort=${sortField},${sortOrder}`;

    if (filter.entitiesIds?.length)
      filter.entitiesIds.forEach((id) => (fromString += `&related_entity_id=${id}`));
    if (filter.unrelatedEntitiesIds?.length)
      filter.unrelatedEntitiesIds.forEach((id) => (fromString += `&unrelated_entity_id=${id}`));

    if (filter.searchTerm) {
      fromString += `&search_term=${filter.searchTerm}`;
    } else {
      if (filter.id) fromString += `&id_has=${filter.id}`;
      if (filter.name) fromString += `&name_has=${filter.name}`;
      if (filter.affiliationTypeId)
        fromString += `&affiliation_type_id=${filter.affiliationTypeId}`;
      if (filter.email) fromString += `&email_has=${filter.email}`;
      if (filter.phone) fromString += `&phone_has=${filter.phone}`;
      if (filter.fax) fromString += `&fax_has=${filter.fax}`;
    }

    return fromString;
  }

  createAffiliation(_affiliation: Affiliation) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const body = {
      address_1: _affiliation.address_1,
      address_2: _affiliation.address_2,
      affiliation_type_id: _affiliation.affiliation_type_id,
      fax: _affiliation.fax,
      note: _affiliation.note,
      description: _affiliation.description,
      email: _affiliation.email,
      phone: _affiliation.phone,
      name: _affiliation.name,
      location_id: _affiliation.location_id,
      city: _affiliation.city,
      state: _affiliation.state,
      zip: _affiliation.zip,
      urbanization: _affiliation.urbanization,
      supervising_control: _affiliation.supervising_control,
      extension: _affiliation.extension,
      office_number: _affiliation.office_number,
    };
    return this.http.post<Affiliation>(
      environment.CORE_BASE + '/companies/' + this.companyId + '/affiliation_groups',
      body,
      {
        headers: httpHeaders,
        observe: 'response',
      },
    );
  }

  createAffiliationType(_affiliation: AffiliationType) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const body = {
      affiliation_type_name: _affiliation.affiliation_type_name,
      affiliation_type_description: _affiliation.affiliation_type_description,
    };
    return this.http.post<AffiliationType>(
      environment.CORE_BASE + '/companies/' + this.companyId + '/affiliation_types',
      body,
      {
        headers: httpHeaders,
        observe: 'response',
      },
    );
  }

  getAllAffiliations(): Observable<Affiliation[]> {
    const url = environment.CORE_BASE + '/all-affiliation';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<Affiliation[]>(url, { headers: httpHeaders });
  }

  getAllDescriptionTypes(): Observable<AffiliationType[]> {
    const url = environment.CORE_BASE + '/companies/' + this.companyId + '/affiliation_types';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<AffiliationType[]>(url, { headers: httpHeaders });
  }

  getAffiliationsNoAdminRole(): Observable<Affiliation[]> {
    const url = environment.CORE_BASE + '/affiliation-no-admin-role';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<Affiliation[]>(url, { headers: httpHeaders });
  }

  getAffiliationById(affiliationId: number): Observable<Affiliation> {
    const body = {
      id: affiliationId,
    };
    const url = environment.CORE_BASE + '/affiliations';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.post<Affiliation>(url, body, { headers: httpHeaders });
  }

  getAffiliationsPage(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: AffiliationsService.getAffiliationsPageQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.companyId}/affiliation_groups`,
      { params },
    );
  }

  findAffiliations(filter, searchTerm: string): Observable<QueryResultsModel> {
    const queryParams = new QueryParamsModel({ ...filter, searchTerm }, 'asc', 'name', 0, 25);

    return this.getAffiliationsPage(queryParams);
  }

  findAffiliationTypes(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const pageSize = queryParams.pageSize;
    const pageIndex = queryParams.pageNumber;
    const searchData = queryParams.filter.searchData;
    let sort = queryParams.sortField.replace(/([-_]\w)/g, (g) => g[1].toUpperCase());
    sort === 'affiliationTypeName' ? (sort = 'name') : sort;
    sort === 'affiliationTypeDescription' ? (sort = 'description') : sort;

    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const params = new HttpParams({
      fromString:
        'page=' +
        pageIndex +
        '&size=' +
        pageSize +
        '&search_term=' +
        searchData +
        '&sort=' +
        sort +
        `,${queryParams.sortOrder}`,
    });
    return this.http.get<QueryResultsModel>(
      environment.CORE_BASE + '/companies/' + this.companyId + '/affiliation_types',
      { headers, params },
    );
  }

  updateAffiliation(_affiliation: Affiliation): Observable<any> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const body: Partial<Affiliation> = {
      address_1: _affiliation.address_1,
      address_2: _affiliation.address_2,
      affiliation_type_id: _affiliation.affiliation_type_id,
      affiliation_type_name: _affiliation.affiliation_type_name,
      affiliation_type_description: _affiliation.affiliation_type_description,
      fax: _affiliation.fax,
      note: _affiliation.note,
      description: _affiliation.description,
      email: _affiliation.email,
      phone: _affiliation.phone,
      name: _affiliation.name,
      location_id: _affiliation.location_id,
      city: _affiliation.city,
      state: _affiliation.state,
      zip: _affiliation.zip,
      urbanization: _affiliation.urbanization,
      supervising_control: _affiliation.supervising_control,
      extension: _affiliation.extension,
      office_number: _affiliation.office_number,
    };
    const id = _affiliation.id;
    return this.http.put(
      environment.CORE_BASE + '/companies/' + this.companyId + '/affiliation_groups/' + id,
      body,
      { headers: httpHeaders },
    );
  }

  updateAffiliationType(_affiliation: AffiliationType): Observable<any> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const body = {
      affiliation_type_name: _affiliation.affiliation_type_name,
      affiliation_type_description: _affiliation.affiliation_type_description,
    };
    const id = _affiliation.affiliation_type_id;
    return this.http.put(
      environment.CORE_BASE + '/companies/' + this.companyId + '/affiliation_types/' + id,
      body,
      { headers: httpHeaders },
    );
  }

  deleteAffiliation(affiliationId: number): Observable<HttpResponse<void>> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    return this.http.delete<void>(
      environment.CORE_BASE +
        '/companies/' +
        this.companyId +
        '/affiliation_groups/' +
        affiliationId,
      {
        headers: httpHeaders,
        observe: 'response',
      },
    );
  }

  deleteAffiliationType(affiliationId: number): Observable<any> {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    return this.http.delete<any>(
      environment.CORE_BASE +
        '/companies/' +
        this.companyId +
        '/affiliation_types/' +
        affiliationId,
      {
        headers: httpHeaders,
      },
    );
  }

  searchAffiliations(term: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const params = new HttpParams({ fromString: 'search_term=' + term });
    return this.http.get<any>(
      environment.CORE_BASE + '/companies/' + this.companyId + '/affiliation_groups',
      { headers, params },
    );
  }

  getAffiliationsForViewInPatient(id, viewContext): Observable<any> {
    let baseURL = '';
    switch (viewContext) {
      case tagsViewContext.COMMUNICATION:
        break;
      case tagsViewContext.ENTITIES:
        baseURL = environment.CORE_BASE;
        break;
      case tagsViewContext.CONTACTS:
        baseURL = environment.CORE_BASE;
        break;
    }
    const url = `${baseURL}/companies/${this.companyId}/${viewContext}/${id}/affiliation_groups`;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.get<any>(url, { headers: httpHeaders });
  }
  getAffiliationsByIds(ids: number[]) {
    let fromString = '';
    ids.forEach((id) => (fromString += `&id=${id}`));
    const params = new HttpParams({ fromString });
    return this.http.get<any>(
      `${environment.CORE_BASE}/companies/${this.companyId}/affiliation_groups`,
      { params },
    );
  }

  getAffiliationTypesPage(query: QueryParamsModel): Observable<QueryResultsModel> {
    const {
      pageSize,
      pageNumber,
      filter: { searchTerm },
      sortField,
      sortOrder,
    } = query;

    const fromString = `sort=name,asc&page=${pageNumber}&size=${pageSize}&search_term=${searchTerm}&sort=${sortField},${sortOrder}`;
    const params = new HttpParams({ fromString });

    return this.http.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.companyId}/affiliation_types`,
      { params },
    );
  }

  searchAffiliationType(searchTerm = ''): Observable<QueryResultsModel> {
    const query = new QueryParamsModel({ searchTerm }, 'asc', 'name', 0, 20);

    return this.getAffiliationTypesPage(query);
  }

  linkUnlinkAffiliation(
    affiliationId: number,
    entityId: number,
    linked: boolean,
  ): Observable<HttpResponse<void>> {
    const body = {
      affiliation_group: { id: affiliationId },
      entity: { id: entityId },
      linked,
    };

    return this.http.put<void>(
      `${environment.CORE_BASE}/companies/${this.companyId}/affiliation_groups/entity_relations/link_unlink`,
      body,
      {
        observe: 'response',
      },
    );
  }
}
